import * as React from "react";
import PropTypes from "prop-types";

import { DateRangePicker } from "@mui/x-date-pickers-pro";
import DatePickerDateInput from "./DateInput";

import { Typography, Divider, Grid, useTheme, Box, Stack } from "@mui/material";
import { translation } from "../Dictionary";
import { AppContext } from "../../contextProviders/AppContext";
import { isValid } from "date-fns";

const RangeShortcutsPanel = ({
  children,
  selectedCompany,
  selectedDateRange,
  theme,
  language,
  endTimeOk,
}) => {
  const CalendarTitleContent = ({ language }) => {
    const TitleText = () => {
      if (!selectedCompany)
        return translation.get(language, "Error: No company");
      if (selectedCompany && !selectedDateRange[0])
        return translation.get(language, "Select pickup date");
      if (
        selectedCompany &&
        selectedDateRange[0] &&
        !selectedDateRange[1] &&
        !endTimeOk.hasError
      )
        return translation.get(language, "Select return date");

      if (endTimeOk.hasError === true) {
        return endTimeOk.message;
      }

      if (selectedCompany && selectedDateRange[0] && selectedDateRange[1])
        return translation.get(language, "Reservation timeframe selected");
    };

    const Title = () => (
      <Typography
        variant="body2"
        sx={{ fontSize: "20px", color: theme.palette.primary.secondaryText }}
        align="center"
      >
        <TitleText />
      </Typography>
    );

    const titleBgColor = () => {
      if (endTimeOk.hasError) {
        return theme.palette.warning.light;
      }
      if (selectedCompany && !selectedDateRange[0]) {
        return theme.palette.primary.dateRangeBgFrom;
      }
      if (selectedCompany && selectedDateRange[0] && !selectedDateRange[1]) {
        return theme.palette.primary.dateRangeBgTo;
      }

      if (selectedCompany && selectedDateRange[0] && selectedDateRange[1]) {
        return theme.palette.primary.dateRangeBgComplete;
      }

      return theme.palette.info.main;
    };

    return (
      <Box
        sx={{
          display: "flex",
          bgcolor: titleBgColor(),
          padding: "5px",
          paddingTop: "15px",
          borderTopLeftRadius: "17px",
          borderTopRightRadius: "17px",
          justifyContent: "center",
        }}
      >
        <Title />
      </Box>
    );
  };

  return (
    <React.Fragment>
      <CalendarTitleContent language={language} />

      <Divider orientation="vertical" />

      {children}
    </React.Fragment>
  );
};

RangeShortcutsPanel.propTypes = {
  children: PropTypes.node,
  setValue: PropTypes.func,
};

export default function DateRange({
  selectedDateRange,
  handleDateChange,
  selectedCompany,
  mobileViewPort,
  selectedPicker,
  setSelectedPicker,
  contentHeigth,
  endTimeOk,
  FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA,
  getMinimumDate,
}) {
  const theme = useTheme();
  const {
    language,
    reservationStart,
    reservationReturn,
    FEATURE_NO_TIME_SELECT,
  } = React.useContext(AppContext);

  const handleCloseCalendar = () => {
    // if selected date is not accepted, the calendar remains open
    if (endTimeOk.hasError) return;
    // if no previous search, open the timepicker after closing the calendar

    if (
      !isValid(reservationStart) &&
      selectedDateRange[1] !== null &&
      !FEATURE_FIXED_RESERVATION_TIMEFRAME_GNISTA
    ) {
      setSelectedPicker("startTime");
    } else {
      setSelectedPicker(null);
    }
  };

  return (
    <DateRangePicker
      desktopModeMediaQuery={"@media (min-width: 1px)"}
      disableMaskedInput
      calendars={mobileViewPort ? 1 : 2}
      disablePast
      minDate={getMinimumDate()}
      disabled={!selectedCompany}
      PaperProps={{
        square: true,
        sx: {
          borderRadius: "17px",
          marginTop: "7px",
        },
      }}
      open={selectedPicker === "calendar"}
      onOpen={() => setSelectedPicker("calendar")}
      onClose={() => handleCloseCalendar()}
      onChange={(newValue) => handleDateChange(newValue)}
      value={selectedDateRange}
      renderInput={(startProps, endProps) => (
        <Stack
          sx={{
            flexDirection: "row",
            flex: 1,
            justifyContent: mobileViewPort ? "center" : "space-between",
            alignItems: "center",
            marginLeft: FEATURE_NO_TIME_SELECT ? "10px" : undefined,
            marginRight: FEATURE_NO_TIME_SELECT ? "10px" : undefined,
          }}
        >
          <DatePickerDateInput
            contentHeigth={contentHeigth}
            id={"start-reservation-picker"}
            pickerProps={startProps}
            selectedPicker={selectedPicker}
            setSelectedPicker={setSelectedPicker}
          />

          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ marginLeft: "10px", marginRight: "10px" }}
          />
          <DatePickerDateInput
            contentHeigth={contentHeigth}
            id={"return-reservation-picker"}
            pickerProps={endProps}
            selectedPicker={selectedPicker}
            setSelectedPicker={setSelectedPicker}
          />
        </Stack>
      )}
      components={{ PaperContent: RangeShortcutsPanel }}
      componentsProps={{
        paperContent: {
          selectedCompany,
          selectedDateRange,
          theme,
          language,
          endTimeOk,
        },
      }}
    />
  );
}
